<template>
  <div
    class="wrapper"
    :class="{ 'opacity-1': show }"
  >
    <!-- New Group by Brand checkbox -->
    <div class="filter group-by-filter">
      <checkbox-item
        label="Group by Brand"
        :checked="groupByBrand"
        @change="toggleGroupByBrand"
      />
    </div>

    <div class="filter-separator" />

    <!-- Other filters -->
    <filter-group
      v-for="(filterOption, index) in filterOptions.filter(option => option.name !== 'Price, $')"
      :key="index"
      :title="filterOption.name"
      :options="filterOption.options"
      :selected-values="selected[filterOption.name] || []"
      :use-row-wrap="filterOption.name === 'Price, $'"
      @change="option => toggleFilter(filterOption.name, option)"
    />

    <div class="filter-separator" />

    <!-- Color Family Filters Component -->
    <color-family-filter
      :products="products"
      :selected-filters="selected"
      @filters-changed="handleColorFiltersChanged"
    />

    <div class="filter-separator" />

    <!-- Price Range Slider -->
    <div class="filter price-slider-filter">
      <price-range-slider
        :min="priceRange.min"
        :max="priceRange.max"
        :model-value="priceRange.current"
        :no-minimum="true"
        :no-maximum="true"
        @change="handlePriceRangeChange"
      />
    </div>
  </div>
</template>
<script>
import constants from '@/constants'
import analytics from '@/@core/utils/analytics'
import PriceRangeSlider from './filters/PriceRangeSlider.vue'
import ColorFamilyFilter from './filters/ColorFamilyFilter.vue'
import FilterGroup from './filters/FilterGroup.vue'
import CheckboxItem from './filters/CheckboxItem.vue'

export default {
  name: 'RetailerAiFilter',
  components: {
    PriceRangeSlider,
    ColorFamilyFilter,
    FilterGroup,
    CheckboxItem,
  },
  props: {
    isLoaded: Boolean,
    filterOptions: { type: Array, default: () => [] },
    selectedFilters: { type: Object, default: () => ({}) },
    groupByBrand: { type: Boolean, default: true },
    products: { type: Array, default: () => [] },
  },
  data() {
    return {
      show: false,
      selected: {},
      priceRange: {
        min: 0,
        max: 200,
        current: [0, 200],
      },
    }
  },
  watch: {
    isLoaded: {
      handler(val) { this.show = val },
      immediate: true,
    },
    selectedFilters: {
      handler(newVal) {
        this.selected = { ...newVal }
        this.priceRange.current = [
          newVal.wholesale_price_from ?? 0,
          newVal.wholesale_price_to ?? 200,
        ]
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    toggleGroupByBrand() {
      this.$emit('toggle-group-by-brand')
    },

    handlePriceRangeChange([min, max]) {
      const filters = { ...this.selected }

      if (min == null && max == null) {
        delete filters.wholesale_price_from
        delete filters.wholesale_price_to
      } else {
        filters.wholesale_price_from = min
        filters.wholesale_price_to = max
      }

      // Track price range change
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_CHANGES_PRICE_RANGE, {
        priceMin: min,
        priceMax: max,
      })

      this.priceRange.current = [min, max]
      this.selected = filters
      this.$emit('filters-changed', filters)
    },

    toggleFilter(group, option) {
      const filters = { ...this.selected }
      filters[group] = filters[group] || []

      const idx = filters[group].indexOf(option.value)
      if (idx === -1) {
        filters[group].push(option.value)
      } else {
        filters[group].splice(idx, 1)
      }

      // Track filter change
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_APPLIES_FILTER, {
        filterGroup: group,
        selectedOption: option.value,
        action: idx === -1 ? 'added' : 'removed',
      })

      this.selected = filters
      this.$emit('filters-changed', filters)
    },

    isChecked(group, option) {
      return Boolean(this.selected[group]?.includes(option.value))
    },

    // Handle color filters changes from ColorFamilyFilter component
    handleColorFiltersChanged(filters) {
      // Track color filter change
      analytics.track(constants.TRACKS.ACTIONS.RETAILER_AI.RETAILER_APPLIES_FILTER, {
        filterGroup: 'Color Family',
        selectedValues: Object.keys(filters).filter(key => key.includes('color')),
      })

      this.selected = filters
      this.$emit('filters-changed', filters)
    },
  },
}
</script>
<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 305px;
  background-color: #fff;
  padding: 14px 20px;
  border-radius: 6px;
  opacity: 0;
  transition: all 0.3s ease;
  position: absolute;
  left: -305px;
}

.opacity-1 {
  opacity: 1;
  left: 0;
  position: relative;
}

.filter {
  font-size: 14px;
  line-height: 21px;
  width: 100%;
  margin-bottom: 16px;
}

.group-by-filter {
  margin-bottom: 8px;
}

.price-slider-filter {
  padding: 0 0 8px 0;
}

.filter-separator {
  height: 1px;
  background-color: #e0e0e0;
  width: 100%;
  margin-bottom: 16px;
}

.type {
  font-weight: 700;
  margin-bottom: 4px;
}

.input {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 4px;
}

.checkbox {
  width: 16px;
  height: 16px;
}

.input-label {
  font-size: 14px;
  line-height: 21px;
}

.row-wrap {
  display: flex;
  justify-content: space-between;
  max-width: 90%;
  flex-wrap: wrap;
  gap: 8px;

  .input  {
    width: 110px;
  }
}

.expand-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
</style>
